import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lightning, ClockCounterClockwise, Microphone, PaperPlaneTilt } from "@phosphor-icons/react";
import { StudioCreateTab } from "@/components/studio/StudioCreateTab";
import { StudioHistoryTab } from "@/components/studio/StudioHistoryTab";
import { StudioPodcastTab } from "@/components/studio/StudioPodcastTab";
import { StudioSyndicateTab } from "@/components/studio/StudioSyndicateTab";

export default function ContentStudioPage() {
  const { api } = useAuth();
  const [availableModels, setAvailableModels] = useState([]);
  const [defaultAudience, setDefaultAudience] = useState("");
  const [defaultTone, setDefaultTone] = useState("Clear, practical, confident");
  const [defaultModel, setDefaultModel] = useState("");

  // Shared package state (set by Create, used by Syndicate)
  const [activePackage, setActivePackage] = useState(null);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setDefaultModel(resp.data.models[0]);
      } catch (e) { console.error("Failed to fetch models:", e); }
    };
    const fetchDefaults = async () => {
      try {
        const resp = await api.get("/profile");
        const p = resp.data;
        setDefaultAudience(p.default_audience || "");
        setDefaultTone(p.default_tone_variations?.join(", ") || "Clear, practical, confident");
        if (p.default_ai_model) setDefaultModel(p.default_ai_model);
      } catch (e) { console.error("Failed to fetch defaults:", e); }
    };
    fetchModels();
    fetchDefaults();
  }, [api]);

  return (
    <>
      <Header />
      <NoBusinessGuard>
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="content-studio-page">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="content-studio-title">
              Content Studio
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
              Idea &rarr; research-backed posts for every channel &rarr; optional syndication &amp; podcast audio.
            </p>
          </div>

          <Tabs defaultValue="create" className="space-y-6">
            <TabsList className="bg-[var(--bg-card)] border border-[var(--border)] p-1 rounded-lg flex-wrap">
              <TabsTrigger value="create" data-testid="tab-create" className="data-[state=active]:bg-[#FF4400] data-[state=active]:text-white rounded-md text-sm px-4 py-2">
                <Lightning size={16} className="mr-1.5" /> Create
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-[#FF4400] data-[state=active]:text-white rounded-md text-sm px-4 py-2">
                <ClockCounterClockwise size={16} className="mr-1.5" /> History
              </TabsTrigger>
              <TabsTrigger value="podcast" data-testid="tab-podcast" className="data-[state=active]:bg-[#FF4400] data-[state=active]:text-white rounded-md text-sm px-4 py-2">
                <Microphone size={16} className="mr-1.5" /> Podcast
              </TabsTrigger>
              <TabsTrigger value="syndicate" data-testid="tab-syndicate" className="data-[state=active]:bg-[#FF4400] data-[state=active]:text-white rounded-md text-sm px-4 py-2">
                <PaperPlaneTilt size={16} className="mr-1.5" /> Syndicate
              </TabsTrigger>
            </TabsList>

            <TabsContent value="create">
              <StudioCreateTab
                api={api}
                availableModels={availableModels}
                defaultAudience={defaultAudience}
                defaultTone={defaultTone}
                defaultModel={defaultModel}
                onPackageCreated={setActivePackage}
              />
            </TabsContent>

            <TabsContent value="history">
              <StudioHistoryTab api={api} onSelectPackage={setActivePackage} />
            </TabsContent>

            <TabsContent value="podcast">
              <StudioPodcastTab api={api} availableModels={availableModels} defaultModel={defaultModel} />
            </TabsContent>

            <TabsContent value="syndicate">
              <StudioSyndicateTab api={api} activePackage={activePackage} />
            </TabsContent>
          </Tabs>
        </main>
      </NoBusinessGuard>
    </>
  );
}
